/**
 * Las rutas.
 *
 * Sin sesión, cualquier ruta que no sea una de autenticación termina en
 * login. El comodín `*` es el que lo garantiza: una URL vieja de una
 * pantalla que todavía no existe no puede quedar en blanco.
 */
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useAuth } from './auth/AuthContext';
import { CommunitiesPage } from './pages/CommunitiesPage';
import { ChallengesPage } from './pages/ChallengesPage';
import { CommunityPage } from './pages/CommunityPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { HomePage } from './pages/HomePage';
import { MatchPage } from './pages/MatchPage';
import { PointsPage } from './pages/PointsPage';
import { LoginPage } from './pages/LoginPage';
import { MorePage } from './pages/MorePage';
import { MemesPage } from './pages/MemesPage';
import { NewCommunityPage } from './pages/NewCommunityPage';
import { ProfilePage } from './pages/ProfilePage';
import { PublicPlayerPage } from './pages/PublicPlayerPage';
import { RegisterPage } from './pages/RegisterPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { AdminInvitationPage } from './pages/AdminInvitationPage';
import { ChallengePostsPage } from './pages/ChallengePostsPage';
import { CourtsPage } from './pages/CourtsPage';
import { PublicBracketPage } from './pages/PublicBracketPage';
import { RivalryPage } from './pages/RivalryPage';
import { BracketFullPage } from './pages/BracketFullPage';
import { TournamentHistoryPage } from './pages/TournamentHistoryPage';
import { TournamentPage } from './pages/TournamentPage';
import { TournamentsPage } from './pages/TournamentsPage';
import { TournamentsTab } from './pages/TournamentsTab';
import { useFeatures } from './hooks/useFeatures';

function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { session } = useAuth();
  const location = useLocation();
  if (session === null) {
    /*
     * SE GUARDA ADÓNDE IBA, y después de entrar se lo lleva ahí.
     *
     * Antes login siempre terminaba en la portada, y eso convertía cualquier
     * enlace compartido en un callejón: quien abre «mirá este torneo» sin
     * sesión entra y aparece en su propia portada, sin el torneo y sin
     * ninguna pista de cuál era. Con las invitaciones a administrar el
     * problema deja de ser una molestia y pasa a perder el enlace del correo,
     * que es lo único que otorga el permiso.
     */
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}

/**
 * Una dirección vieja de «/comunidades/…» llevada a «/clubes/…».
 *
 * Se arma con lo que venga después: `/comunidades/<id>/torneos` termina en
 * `/clubes/<id>/torneos`. Escribir una redirección por ruta sería tener que
 * acordarse de agregar la de cada ruta nueva, y nadie se acuerda.
 */
function ComunidadesViejas(): JSX.Element {
  const resto = useParams()['*'] ?? '';
  return <Navigate to={`/clubes/${resto}`} replace />;
}

export function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* LA DIRECCIÓN QUE SALTEA LA PUERTA DE INSTALACIÓN.
          Muestra el login, igual que `/login`: lo que la hace distinta es que
          `InstalarPrimero` la reconoce y deja pasar. Ver `RUTA_SIN_PUERTA`.
          Es una ruta propia y NO una redirección a `/login` porque quien la
          guarde en su pantalla de inicio tiene que poder volver a abrirla. */}
      <Route path="/mobile" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* EL TABLÓN DE «busco partido». Pide sesión: es de la aplicación, no
          público, y el botón de WhatsApp pide saber quién lo toca. */}
      <Route
        path="/tablon"
        element={
          <RequireAuth>
            <ChallengePostsPage />
          </RequireAuth>
        }
      />
      {/* Pide sesión: el permiso se le da a una cuenta. Ver la pantalla. */}
      <Route
        path="/canchas"
        element={
          <RequireAuth>
            <CourtsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/invitacion-admin"
        element={
          <RequireAuth>
            <AdminInvitationPage />
          </RequireAuth>
        }
      />
      <Route
        path="/"
        element={
          <RequireAuth>
            <HomePage />
          </RequireAuth>
        }
      />
      {/* Las rutas de adentro van en español porque son las que la gente ve
          y comparte. Las de autenticación quedan en inglés: son las que
          viajan en los correos ya enviados y cambiarlas rompe esos enlaces.

          SE LLAMABAN «/comunidades» Y AHORA SON «/clubes», porque el producto
          entero dejó de decir «comunidad». Las viejas siguen contestando, más
          abajo: un club se comparte por WhatsApp y esos enlaces ya están
          mandados; renombrar la ruta sin dejar la anterior los rompe todos de
          golpe y quien los abre no ve un club, ve la pantalla de entrar. */}
      <Route
        path="/clubes"
        element={
          <RequireAuth>
            <CommunitiesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/clubes/nuevo"
        element={
          <RequireAuth>
            <NewCommunityPage />
          </RequireAuth>
        }
      />
      {/* Va DESPUÉS de «/clubes/nuevo»: si estuviera antes, `:id` se
          comería la palabra «nueva» y crear una comunidad abriría la ficha de
          una comunidad con ese id, que no existe. */}
      <Route
        path="/clubes/:id"
        element={
          <RequireAuth>
            <CommunityPage />
          </RequireAuth>
        }
      />
      <Route
        path="/clubes/:id/torneos"
        element={
          <RequireAuth>
            <TournamentsPage />
          </RequireAuth>
        }
      />
      {/* La PESTAÑA de torneos, que es un desvío al club de la persona. Va
          antes que «/torneos/:id» por lo mismo que «/clubes/nuevo»: si
          estuviera después no importaría —son rutas distintas— pero leídas
          en orden, la general primero y la del torneo después es como se
          entiende que una es la pestaña y la otra una ficha. */}
      <Route
        path="/torneos"
        element={
          <RequireAuth>
            <TournamentsTab />
          </RequireAuth>
        }
      />
      <Route
        path="/torneos/:id"
        element={
          <RequireAuth>
            <TournamentPage />
          </RequireAuth>
        }
      />
      {/* EL CUADRO EN GRANDE, sin barra ni pestañas: de ahí sale el espacio.
          Va después de «/torneos/:id» y no antes porque son rutas distintas,
          pero leídas en orden se entiende que una es la ficha y la otra su
          cuadro a pantalla completa. */}
      {/* EL HISTORIAL, antes de «/torneos/:id»: leído en orden, «historial»
          es una palabra y no el id de un torneo. Nest y React Router
          resuelven distinto, pero el orden se lee igual. */}
      <Route
        path="/torneos/historial"
        element={
          <RequireAuth>
            <TournamentHistoryPage />
          </RequireAuth>
        }
      />
      <Route
        path="/torneos/:id/cuadro"
        element={
          <RequireAuth>
            <BracketFullPage />
          </RequireAuth>
        }
      />
      {/*
        EL CUADRO PÚBLICO, SIN `RequireAuth` — y eso es todo el cambio de
        ruta, porque la pantalla consume un endpoint que tampoco pide sesión.
        Es el enlace que se manda por WhatsApp: del otro lado lo abre gente
        que no está en la aplicación, y con `RequireAuth` ese enlace llevaba a
        la pantalla de ingreso, que se lee como un enlace roto.

        `/cuadro/:id` y no `/torneos/:id/cuadro-publico`: es la dirección que
        alguien pega en un chat, y cuanto más corta, menos se rompe al
        copiarla. Que no cuelgue de «/torneos» tampoco es casual — todo lo que
        cuelga de ahí pide sesión, y esta no.
      */}
      <Route path="/cuadro/:id" element={<PublicBracketPage />} />
      <Route
        path="/retos"
        element={
          <RequireAuth>
            <ChallengesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/mas"
        element={
          <RequireAuth>
            <MorePage />
          </RequireAuth>
        }
      />
      {/* El perfil de OTRA persona. «/perfil» es el propio y este es el
          ajeno: dos pantallas distintas porque responden dos preguntas
          distintas —una se edita, la otra se mira. */}
      {/* EL CARA A CARA, colgando del perfil de esa persona: es una pregunta
          SOBRE ELLA —«¿cómo me va contra este?»— y el otro lado sale de la
          sesión. Va antes que «/jugadores/:id» por lo mismo que las otras:
          leídas en orden se entiende que una es la ficha y la otra un detalle
          suyo. */}
      {/* UN PARTIDO SOLO. `torneo` o `reto` en la dirección porque son dos
          tablas distintas: ver `rutaDelPartido`. */}
      {/* CÓMO FUNCIONAN LOS PUNTOS. Abierta a cualquiera con cuenta: son
          las reglas del juego, y esconderlas detrás de un rol sería pedirle a
          la gente que confíe en una cuenta que no puede ver. */}
      <Route
        path="/puntos"
        element={
          <RequireAuth>
            <PointsPage />
          </RequireAuth>
        }
      />
      {/* EL MURO DE MEMES. No pide club ni categoría: es de toda la comunidad,
          y por eso alcanza con tener cuenta.

          Y SE PUEDE APAGAR desde la administración de Tennis Up Club. La ruta se
          cierra además de esconder la tarjeta: esconder el enlace y dejar la
          dirección viva es apagar algo a medias, y quien la tenga guardada
          —o en el historial del teléfono— entra igual. */}
      <Route
        path="/memes"
        element={
          <RequireAuth>
            <SiHayMemes>
              <MemesPage />
            </SiHayMemes>
          </RequireAuth>
        }
      />
      <Route
        path="/partidos/:kind/:id"
        element={
          <RequireAuth>
            <MatchPage />
          </RequireAuth>
        }
      />
      <Route
        path="/jugadores/:id/vs"
        element={
          <RequireAuth>
            <RivalryPage />
          </RequireAuth>
        }
      />
      {/* EL CARA A CARA DE DOS PERSONAS CUALESQUIERA. La misma pantalla: lo
          único que cambia es quién va de cada lado. Ver `RivalryPage`. */}
      <Route
        path="/jugadores/:id/vs/:otherId"
        element={
          <RequireAuth>
            <RivalryPage />
          </RequireAuth>
        }
      />
      <Route
        path="/jugadores/:id"
        element={
          <RequireAuth>
            <PublicPlayerPage />
          </RequireAuth>
        }
      />
      <Route
        path="/perfil"
        element={
          <RequireAuth>
            <ProfilePage />
          </RequireAuth>
        }
      />
      {/*
        LAS DIRECCIONES VIEJAS, que siguen abriendo lo mismo.

        `replace` para que el botón de atrás no devuelva a la redirección y
        rebote otra vez hacia adelante. El `*` del final arrastra lo que
        venga después —el id del club, sus torneos— sin tener que repetir
        cada ruta acá.
      */}
      <Route path="/comunidades/nueva" element={<Navigate to="/clubes/nuevo" replace />} />
      <Route path="/comunidades" element={<Navigate to="/clubes" replace />} />
      <Route path="/comunidades/*" element={<ComunidadesViejas />} />

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

/**
 * EL MURO DE MEMES, SI ESTÁ PRENDIDO. Apagado, al inicio.
 *
 * `replace` Y NO UN EMPUJÓN NORMAL: sin eso, el botón de volver del teléfono
 * devuelve a `/memes`, que vuelve a mandar al inicio, que… La persona queda
 * atrapada sin entender por qué no puede salir.
 *
 * MIENTRAS NO SE SABE, SE DEJA PASAR. `useFeatures` arranca en prendido, así
 * que una conexión lenta no expulsa a nadie de una pantalla que sí existe.
 */
function SiHayMemes({ children }: { children: JSX.Element }): JSX.Element {
  const features = useFeatures();
  return features.memes ? children : <Navigate to="/" replace />;
}
